import { fieldStateCss } from '@src/components/common/fieldStateStyles';
import { useStore, useUserIdentity } from '@src/stores-v2/StoreContext';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { forIPad } from '@src/utility/styled';
import _orderBy from 'lodash/orderBy';
import _uniqBy from 'lodash/uniqBy';
import { observer } from 'mobx-react-lite';
import React, { useCallback } from 'react';
import { Tooltip } from 'react-tooltip';
import styled, { css } from 'styled-components';

import ChevronDownIcon from '../../svgs/ChevronDownIcon';
import MoreVertIcon from '../../svgs/MoreVertIcon';
import PaperPlaneIcon from '../../svgs/PaperPlaneIcon';
import SimplePencilIcon from '../../svgs/SimplePencilIcon';
import { AspectRatio } from '../../types.ts/video';
import { convertTimeToMMSS } from '../../utility/timeFormat';
import { useFlagsCombination } from '../../utility/useFlagsCombination';
import {
  ContextMenuItem,
  ContextMenuSurface,
  KebabButton,
} from '../common/contextMenuStyles';
import Modal from '../common/Modal';
import { COMPACT_TOOLTIP_STYLE } from '../common/tooltipStyles';
import SpinningLoading from '../SpinningLoading';
import { useOutsideAlerter } from '../transcript/useClickOutside';
import { XmlUpload } from '../xmlparser/XmlUpload';
import CopyContentModal from './CopyContentModal';
import { DeleteVideosModal } from './DeleteVideosModal';
import {
  type DropdownOption,
  useVideoSelector,
} from './hooks/useVideoSelector';
import LastVideoAction from './LastVideoAction';

import type { VideoAction } from '../../types.ts/story';

type ArPillStatus = 'published' | 'draft' | 'missing';
type ArPill = { format: string; status: ArPillStatus; videoId?: string };

// Fixed pill order — matches the bottom-stage AspectRatiosDropdown.
const CORE_ASPECT_RATIOS: string[] = [
  AspectRatio.AR_16_9,
  AspectRatio.AR_9_16,
  AspectRatio.AR_1_1,
];

export const VideoSelector: React.FC = observer(() => {
  const userIdentityStore = useStore().userIdentityStore;
  const videoCreator = useVideoCreatorStore();
  const userIdentity = useUserIdentity();
  const { appEnableApprovedButton, enableImportXml, renderUsingChunks } =
    useFlagsCombination(userIdentity.currentRole);

  // Use the custom hook for all non-presentational logic
  const {
    // State
    dropdownOpen,
    setDropdownOpen,
    newVideoModal,
    setNewVideoModal,
    actionProgress,
    setActionProgress,
    editingCurrentVideo,
    setEditingCurrentVideo,
    currentVideoTitle,
    setCurrentVideoTitle,
    optionsModal,
    setOptionsModal,
    videoToDelete,
    setVideoToDelete,

    // Refs
    inputRef,
    containerRef,
    buttonRef,
    editingCurrentVideoRef,

    // Data
    allVideoOptions,
    currentVideoOption,
    displayedTitle,

    // Actions
    copyVideo,
    deleteSelectedVideos,
    makeFeatureVideo,
    selectVideo,
    handleRenameCurrentVideo,

    // Event handlers
    handleShowEditInput,
    toggleEditingInput,
  } = useVideoSelector({ videoCreator, renderUsingChunks });

  const storyFinalVideo = videoCreator.story?.finalVideo;

  useOutsideAlerter(buttonRef, () => {
    setOptionsModal(null);
  });

  function getClipType(video: DropdownOption) {
    let videoId = video.id;
    if (!videoId) return null;
    if (videoId === 'original_story') return 'Original';
    if (videoId === storyFinalVideo?.id) return 'Feature';
    return null;
  }
  const getVideoData = useCallback((video: DropdownOption) => {
    if (!video.id && !video.associatedVideos?.length) return [];
    const data: {
      id: string;
      format: AspectRatio;
      last_action_date: Date | null;
      isPublished: boolean;
    }[] = [];
    if (video.id && video.id !== 'original_story') {
      data.push({
        id: video.id,
        format:
          video.videoFilePrimary?.customData?.aspectRatio || video?.aspectRatio,
        last_action_date: video.lastAction?.date
          ? new Date(video.lastAction.date)
          : null,
        isPublished: !!video.videoFilePrimary,
      });
    }

    if (video.associatedVideos?.length) {
      const associatedVideos =
        video.associatedVideos.map(v => ({
          id: v.id!,
          format: v.videoFilePrimary?.customData.aspectRatio || v.aspectRatio,
          last_action_date: v.lastActionJson?.date
            ? new Date(v.lastActionJson.date)
            : new Date(0),
          isPublished: !!v.videoFilePrimary,
        })) || [];

      data.push(...associatedVideos);
    }

    return data;
  }, []);

  const handleSelectVideo = (video: DropdownOption) => {
    if (!video.id && !video.associatedVideos?.length) return null;
    if (video.id === 'original_story') {
      selectVideo(video.id);
      return;
    } else {
      const data = getVideoData(video);

      const videoData = _orderBy(
        _uniqBy(data, 'format'),
        'last_action_date',
        'desc',
      );

      selectVideo(videoData?.[0]?.id);
    }
  };

  // One pill per core aspect ratio: published beats draft, then most recent
  // action; formats outside the core three (e.g. 4:5) append after with their
  // real status. Missing core formats still render so the three states read
  // side by side.
  const getAspectRatioPills = useCallback(
    (video: DropdownOption): ArPill[] => {
      const variants = getVideoData(video);
      const bestByFormat = new Map<string, (typeof variants)[number]>();
      for (const variant of variants) {
        const current = bestByFormat.get(variant.format);
        if (
          !current ||
          (variant.isPublished && !current.isPublished) ||
          (variant.isPublished === current.isPublished &&
            (variant.last_action_date?.getTime() ?? 0) >
              (current.last_action_date?.getTime() ?? 0))
        ) {
          bestByFormat.set(variant.format, variant);
        }
      }

      const toPill = (
        format: string,
        variant: (typeof variants)[number],
      ): ArPill => ({
        format,
        status: variant.isPublished ? 'published' : 'draft',
        videoId: variant.id,
      });

      const pills: ArPill[] = CORE_ASPECT_RATIOS.map(format => {
        const variant = bestByFormat.get(format);
        return variant
          ? toPill(format, variant)
          : { format, status: 'missing' };
      });

      for (const [format, variant] of bestByFormat) {
        if (!CORE_ASPECT_RATIOS.includes(format)) {
          pills.push(toPill(format, variant));
        }
      }

      return pills;
    },
    [getVideoData],
  );

  function renderVideoDimensions(video: DropdownOption) {
    if (video.id === 'original_story') return null;
    if (!video.id && !video.associatedVideos?.length) return null;

    return (
      <>
        {getAspectRatioPills(video).map(pill => {
          const { videoId } = pill;
          const isMissing = pill.status === 'missing' || !videoId;
          return (
            <VideoAspectRatio
              className="clip-aspect-ratio"
              data-status={pill.status}
              key={pill.format}
              status={pill.status}
              role={isMissing ? undefined : 'button'}
              aria-disabled={isMissing || undefined}
              data-tooltip-id="video-selector-tooltip"
              data-tooltip-content={
                pill.status === 'published'
                  ? `${pill.format} · Published`
                  : pill.status === 'draft'
                    ? `${pill.format} · Draft — not published yet`
                    : `${pill.format} · Not created`
              }
              onClick={
                isMissing
                  ? // Not-created formats are inert: swallow the click so it
                    // doesn't bubble to the row's select handler.
                    e => e.stopPropagation()
                  : async e => {
                      e.stopPropagation();
                      setDropdownOpen(false);
                      await videoCreator.loadVideo(videoId);
                    }
              }
            >
              {pill.format}
            </VideoAspectRatio>
          );
        })}
      </>
    );
  }

  const renderDropdownItem = (video: DropdownOption) => {
    const allLastActions = [
      video.lastAction,
      ...(video.associatedVideos?.map(v => v.lastActionJson) || []),
    ].filter(Boolean) as VideoAction[];
    const mostRecent = allLastActions.length
      ? allLastActions.reduce((latest, current) =>
          !latest || new Date(current.date) > new Date(latest.date)
            ? current
            : latest,
        )
      : null;
    const clipType = getClipType(video);
    const isSelected = video.id === currentVideoOption?.id;
    const isOriginal = video.id === 'original_story';

    const closeAndSelect = () => {
      setDropdownOpen(false);
      handleSelectVideo(video);
    };

    return (
      <DropdownItem
        key={video.id}
        isSelected={isSelected}
        onClick={closeAndSelect}
      >
        <DropdownItemLeft>
          <Title isSelected={isSelected}>
            {clipType && <ClipType>{clipType}</ClipType>}
            <div className="clip-title">{video.title}</div>
          </Title>
          {mostRecent && <LastVideoAction lastAction={mostRecent} />}
        </DropdownItemLeft>
        <DropdownItemRight>
          {isOriginal && (
            <DuplicateOriginalButton
              onClick={e => {
                e.stopPropagation();
                closeAndSelect();
              }}
            >
              Duplicate Original
            </DuplicateOriginalButton>
          )}
          <VideoIconsAndAspectRatios>
            {appEnableApprovedButton &&
              video.isClientReady &&
              userIdentityStore.isInternalUser() && (
                <PaperPlaneIconContainer
                  data-tooltip-id="video-selector-tooltip"
                  data-tooltip-content="Sent to Client"
                >
                  <PaperPlaneIcon
                    strokeColor="var(--accent)"
                    width="14"
                    height="14"
                  />
                </PaperPlaneIconContainer>
              )}
            {renderVideoDimensions(video)}
          </VideoIconsAndAspectRatios>
          <TimeAndEllipsis>
            <Duration>
              {video.duration ? convertTimeToMMSS(video.duration) : '--:--'}
            </Duration>

            <MoreActionsButton
              data-tooltip-id="video-selector-tooltip"
              data-tooltip-content="More Actions"
              aria-label="Clip actions"
              onClick={e => {
                const rect = e.currentTarget.getBoundingClientRect();
                setOptionsModal({
                  position: rect.bottom,
                  right: window.innerWidth - rect.right,
                  video,
                });
                e.stopPropagation();
              }}
            >
              <MoreVertIcon />
            </MoreActionsButton>
          </TimeAndEllipsis>
        </DropdownItemRight>
      </DropdownItem>
    );
  };

  const renderActionDropdown = (
    video: DropdownOption | null,
    top: number | null = null,
    right: number | null = null,
  ) => (
    <ActionsMenu ref={buttonRef} top={top} right={right}>
      <ContextMenuItem
        onClick={e => {
          setNewVideoModal({ type: 'rename', video });
          e.stopPropagation();
        }}
      >
        Rename
      </ContextMenuItem>
      <ContextMenuItem
        onClick={e => {
          setNewVideoModal({ type: 'save_as', video });
          e.stopPropagation();
        }}
      >
        Save As
      </ContextMenuItem>
      <ContextMenuItem onClick={() => makeFeatureVideo(video?.id!)}>
        Make Feature Story
      </ContextMenuItem>
      <ContextMenuItem
        $danger
        onClick={e => {
          e.stopPropagation();
          setVideoToDelete(video);
          setOptionsModal(null);
        }}
      >
        Delete
      </ContextMenuItem>
    </ActionsMenu>
  );

  return (
    <Wrapper>
      <Item
        onBlur={handleRenameCurrentVideo}
        isEditing={editingCurrentVideo}
        ref={containerRef}
      >
        {editingCurrentVideoRef.current ? (
          <>
            <PrimaryVideoInput
              ref={inputRef}
              value={currentVideoTitle}
              placeholder="Enter clip name"
              onChange={e => setCurrentVideoTitle(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  handleRenameCurrentVideo();
                  setEditingCurrentVideo(false);
                  editingCurrentVideoRef.current = false;
                } else if (e.key === 'Escape') {
                  setCurrentVideoTitle(displayedTitle);
                  setEditingCurrentVideo(false);
                  editingCurrentVideoRef.current = false;
                }
              }}
            />
          </>
        ) : (
          <>
            {/* The name opens the clips dropdown, the pencil renames. Opening
                the list is the far more frequent intent, so it takes the row's
                large target and the chevron's promise now matches the text
                beside it; renaming moved onto the pencil that was already the
                hover-revealed signal for it. Clicking the name used to open the
                rename input, which put a ~215px target on the rare action right
                beside a 20px one for the common one — every leftward miss on the
                chevron renamed instead, and that path autofocuses an input,
                which costs a canvas edit (pitfall 10) and commits on blur. */}
            {/* No aria-label: the accessible name is the clip's own title, which
                is the useful thing to announce. The chevron carries the "All
                Clips" name for the same action, so the pair reads as a name and
                its disclosure rather than two identically-named buttons. */}
            <TitleClickArea
              title="All Clips"
              aria-haspopup="listbox"
              aria-expanded={dropdownOpen}
              onClick={e => {
                e.stopPropagation();
                setDropdownOpen(open => !open);
              }}
            >
              <MainTitle active={dropdownOpen}>{displayedTitle}</MainTitle>
            </TitleClickArea>
            <RenameButton
              type="button"
              title="Rename Clip"
              aria-label="Rename this clip"
              onClick={e => {
                e.stopPropagation();
                handleShowEditInput();
              }}
            >
              <SimplePencilIcon width="14" height="14" />
            </RenameButton>
            <DropdownChevronButton
              type="button"
              aria-label="All Clips"
              aria-haspopup="listbox"
              aria-expanded={dropdownOpen}
              onClick={e => {
                e.stopPropagation();
                setDropdownOpen(open => !open);
              }}
            >
              <ChevronRotator $isOpen={dropdownOpen}>
                <ChevronDownIcon width="12" height="12" />
              </ChevronRotator>
            </DropdownChevronButton>
          </>
        )}
      </Item>

      {editingCurrentVideoRef.current && (
        <DropdownBackground
          onClick={() => {
            toggleEditingInput();
            setDropdownOpen(false);
          }}
        />
      )}

      {dropdownOpen && (
        <>
          <DropdownBackground
            onClick={() => {
              setDropdownOpen(false);
              setOptionsModal(null);
            }}
          />
          <Dropdown>
            {/* Sized down from the default 72px ring: at full size it fills a
                dropdown this short and reads as breakage rather than a wait. */}
            {!!actionProgress && (
              <SpinningLoading
                text={actionProgress}
                spinnerSize="32px"
                spinnerCircleStyle={{
                  borderWidth: '3px',
                  boxSizing: 'border-box',
                }}
                contentStyle={{ gap: '12px' }}
              />
            )}
            <DropdownScrollableArea>
              {allVideoOptions.map(video => renderDropdownItem(video))}
              {enableImportXml && (
                <DropdownItem
                  onClick={() => {
                    // setDropdownOpen(false);
                  }}
                >
                  <XmlUpload
                    onUpload={() => {
                      setDropdownOpen(false);
                    }}
                  />
                </DropdownItem>
              )}
            </DropdownScrollableArea>
          </Dropdown>
          {optionsModal?.video &&
            renderActionDropdown(
              optionsModal.video,
              optionsModal.position,
              optionsModal.right,
            )}
          <Tooltip
            id="video-selector-tooltip"
            place="top"
            noArrow
            opacity={1}
            positionStrategy="fixed"
            style={COMPACT_TOOLTIP_STYLE}
          />
        </>
      )}

      {newVideoModal && (
        <Modal
          isOpen={newVideoModal !== null}
          closeModal={() => setNewVideoModal(null)}
        >
          <CopyContentModal
            name={
              (newVideoModal.video?.id === 'original_video' ? 'Copy of ' : '') +
              newVideoModal.video?.title
            }
            tag="Clip"
            type={newVideoModal.type}
            onCancel={() => setNewVideoModal(null)}
            onSave={async storyName => {
              setNewVideoModal(null);
              if (newVideoModal?.type === 'rename') {
                setActionProgress('Renaming video…');
                await videoCreator.renameVideo(
                  newVideoModal.video?.id!,
                  storyName,
                );
              } else {
                setActionProgress('Copying video data…');
                await copyVideo(newVideoModal.video!, storyName);
              }
              setActionProgress('');

              setDropdownOpen(false);
            }}
          />
        </Modal>
      )}

      {videoToDelete && (
        <DeleteVideosModal
          isOpen={!!videoToDelete}
          onClose={() => {
            setVideoToDelete(null);
          }}
          onConfirm={async selectedVideoIds => {
            setActionProgress('Deleting videos…');
            await deleteSelectedVideos(selectedVideoIds, videoToDelete);
            setActionProgress('');
            setVideoToDelete(null);
          }}
          mainVideo={videoToDelete}
        />
      )}
    </Wrapper>
  );
});

/**
 * The rename trigger (Articles title rename pattern — see BlogView's
 * TitleClickArea/EditTitleButton). A real button rather than decoration inside
 * the title, which is what lets the title itself open the clips dropdown; it is
 * therefore a SIBLING of `TitleClickArea`, not a child — a button cannot nest.
 *
 * Revealed on hover of the whole row (see `Item`) rather than of the title alone,
 * so it is already showing by the time the pointer is anywhere near it, and by
 * keyboard focus so it is reachable without a pointer at all. It holds its space
 * while transparent, so the row does not reflow on hover.
 */
const RenameButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  width: 24px;
  height: 24px;
  padding: 0;
  border: none;
  border-radius: 6px;
  background: none;
  color: var(--text-muted);
  cursor: pointer;
  flex-shrink: 0;
  opacity: 0;
  transition:
    opacity var(--transition-fast) ease,
    background var(--transition-fast) ease,
    color var(--transition-fast) ease;

  &:hover {
    background: var(--surface-elevated);
    color: var(--foreground);
  }

  &:focus-visible {
    opacity: 1;
  }

  /* No hover to reveal it on a touch surface, and the pencil is the only inline
     way to rename — the alternative is the per-clip kebab in the dropdown. */
  ${forIPad(`
    opacity: 1;
  `)}
`;

// Opens the clips dropdown, like the chevron beside it: the name is what a
// creator aims at when they want the list, and it is the row's large target.
const TitleClickArea = styled.button.attrs({ type: 'button' })`
  display: flex;
  align-items: center;
  flex: 0 1 auto;
  min-width: 0;
  background: none;
  /* A transparent border of the input's own width rather than none: the open
     field carries 1px, so without it the name steps sideways and up by a pixel
     the moment a rename opens. The same invariant the shared control keeps —
     identical padding and border on both states (PLAT-11503).
     Backtick-free on purpose: this sits inside a template literal. */
  border: 1px solid transparent;
  padding: 4px 8px;
  border-radius: var(--radius-input);
  color: inherit;
  font-family: inherit;
  cursor: pointer;
  transition: background var(--transition-fast) ease;

  &:hover {
    background: var(--sidebar-hover);
  }
`;

// The other half of the same disclosure; gold stays — it's the Creator Studio
// disclosure color. 24px box (the icon is 12px): at the old 20px it was under a
// comfortable minimum, which is what made it easy to miss into the title.
const DropdownChevronButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  width: 24px;
  height: 24px;
  background: none;
  border: none;
  color: var(--accent);
  cursor: pointer;
  padding: 0;
  border-radius: 6px;
  flex-shrink: 0;
  transition: background var(--transition-fast) ease;

  &:hover {
    background: var(--surface-elevated);
  }
`;

const ChevronRotator = styled.span<{ $isOpen: boolean }>`
  display: flex;
  transition: rotate var(--transition-fast) ease;

  ${props => props.$isOpen && `rotate: 180deg;`}
`;

const Wrapper = styled.div`
  font-family: var(--font-primary);
  position: relative;
  z-index: 100002;
  width: fit-content;
  max-width: 440px;
  height: fit-content;
  display: flex;
  align-items: center;
  gap: 8px;

  ${forIPad(`
    max-width: 280px;
  `)}
`;

// Story Manager kebab treatment, without its built-in left margin so it sits
// flush in the compact row.
const MoreActionsButton = styled(KebabButton)`
  margin-left: 0;
  padding: var(--spacing-2xs);
`;

const MainTitle = styled.div<{ active: boolean }>`
  font-size: var(--text-sm);
  font-weight: var(--font-weight-medium);
  color: var(--foreground);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 390px;
`;

const Title = styled.div<{ isSelected: boolean }>`
  font-size: var(--text-sm);
  font-weight: var(--font-weight-regular);
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const PaperPlaneIconContainer = styled.div`
  display: flex;
  align-self: center;
  margin: 0 8px;
`;

const Duration = styled.div`
  /* 11px meta size — precedent: ExportMenu FixedHint, UnfolderedStoryRow */
  font-size: 11px;
  font-style: normal;
  font-weight: var(--font-weight-medium);
  line-height: normal;
  /* The aspect-ratio pills are all fixed-width, but the row's right block is
     right-anchored, so this is the only element whose width can move them.
     Proxima Nova's default figures are proportional ("1" is barely half of
     "0"), which drifted the pill columns by up to ~1.5px per row. Tabular
     figures give every MM:SS the same advance; the floor covers the "--:--"
     fallback so it measures the same too (PLAT-11536). */
  font-variant-numeric: tabular-nums;
  min-width: 30px;
  text-align: right;
  // white-space: nowrap;
`;

const TimeAndEllipsis = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  margin-left: var(--spacing-sm);
`;

// Pure layout container — hover/click live on the title pill, the pencil and the
// chevron button, per the Articles header interaction. The one exception is the
// pencil's reveal: hovering ANYWHERE on the row shows it, so it is visible before
// the pointer arrives rather than only once it is already over the title.
const Item = styled.div<{ isEditing: boolean }>`
  border: none;
  border-radius: var(--radius);
  /* Constant: the resting trigger and the open input now share a box, so there
     is no longer a taller editing state to give padding back to (PLAT-11503). */
  padding: 2px 0;
  position: relative;
  box-sizing: border-box;
  display: flex;
  gap: 2px;
  align-items: center;

  &:hover ${RenameButton} {
    opacity: 1;
  }

  ${props => !props.isEditing && forIPad(`padding: 2px 0;`)}
`;

const Dropdown = styled.div`
  position: absolute;
  z-index: 100002;
  top: calc(100% + 1px);
  left: 6px;
  width: 630px;
  max-width: 90vw;
  border: 1px solid var(--border);
  border-radius: 8px;
  background-color: var(--background);
  padding: 8px 0;
  box-sizing: border-box;
  box-shadow: 0 8px 32px var(--overlay-dark);
`;

const DropdownScrollableArea = styled.div`
  max-height: 500px;
  display: flex;
  flex-direction: column;
  overflow-y: auto;

  @media only screen and (max-height: 950px) {
    max-height: 450px;
  }
  @media only screen and (max-height: 810px) {
    max-height: 400px;
  }
  @media only screen and (max-height: 700px) {
    max-height: 350px;
  }
  @media only screen and (max-height: 600px) {
    max-height: 300px;
  }
  @media only screen and (max-height: 500px) {
    max-height: 250px;
  }
  @media only screen and (max-height: 430px) {
    max-height: 200px;
  }
  @media only screen and (max-height: 344px) {
    max-height: 150px;
  }
`;

const DropdownBackground = styled.div`
  position: fixed;
  z-index: -1;
  inset: 0;
  box-sizing: border-box;
  background-color: var(--modal-backdrop);
  cursor: pointer;
`;

const DropdownItem = styled.div<{ isSelected?: boolean }>`
  font-size: var(--text-xs);
  font-weight: var(--font-weight-regular);
  line-height: normal;
  padding: 6px 16px;
  display: flex;
  gap: 8px;
  position: relative;
  cursor: pointer;
  transition: var(--transition-fast);
  background-color: var(--background);
  color: var(--text-secondary);

  /* Export-panel hierarchy (ExportMenu setting rows): medium-weight primary
     title a shade above the helper text. Selection is carried by the gold
     color alone. 13px sits between the text tokens (precedent:
     MainMenuNavList, SidebarActiveStoryCard). */
  .clip-title {
    color: ${props =>
      props.isSelected ? 'var(--accent)' : 'var(--text-secondary)'};
    font-size: 13px;
    font-weight: var(--font-weight-medium);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  p {
    transition: var(--transition-fast);
  }

  &:hover {
    background-color: var(--surface-elevated);
  }

  &:not(:hover):not(:last-child)::after {
    content: '';
    position: absolute;
    height: 1px;
    width: calc(100% - 32px);
    left: 16px;
    background-color: var(--border);
    bottom: -1px;
  }
`;

const DropdownItemLeft = styled.div`
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const DropdownItemRight = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 4px;
  margin-left: auto;
`;

const ClipType = styled.div`
  display: flex;
  gap: 5px;
  align-items: center;
  font-size: var(--text-xs);
  font-weight: var(--font-weight-regular);
`;

const ActionsMenu = styled(ContextMenuSurface)<{
  top: number | null;
  right: number | null;
}>`
  top: ${props => (props.top ? props.top : 80)}px;
  right: ${props => (props.right != null ? props.right : 20)}px;
  left: auto;
  /* above the Dropdown's 100002 (surface default is 100001) */
  z-index: 100004;
`;

const VideoIconsAndAspectRatios = styled.div`
  display: flex;
  gap: 5px;
  margin-left: auto;
`;

const DuplicateOriginalButton = styled.button.attrs({ type: 'button' })`
  display: flex;
  align-items: center;
  background: transparent;
  border: 1px solid var(--text-muted);
  color: var(--text-muted);
  border-radius: 4px;
  padding: 0 8px;
  height: 24px;
  cursor: pointer;
  font-family: inherit;
  font-size: var(--text-xs);
  font-weight: var(--font-weight-medium);
  white-space: nowrap;
  transition: var(--transition-fast);

  &:hover {
    background: var(--surface-elevated);
    color: var(--foreground);
  }
`;

const VideoAspectRatio = styled.div<{ status: ArPillStatus }>`
  /* Sized to fit the widest core label (16:9) so all three pills render at
     the same width and stay column-aligned across rows (PLAT-11536). */
  min-width: 38px;
  height: 22px;
  padding: 0 4px;
  box-sizing: border-box;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 4px;
  font-weight: var(--font-weight-medium);
  font-size: var(--text-xs);
  line-height: 1;
  transition: var(--transition-fast);

  ${({ status }) =>
    status === 'published'
      ? css`
          cursor: pointer;
          border: none;
          background-color: var(--primary);
          color: var(--background);

          &:hover {
            background-color: var(--primary-hover);
          }
        `
      : status === 'draft'
        ? css`
            cursor: pointer;
            border: none;
            background-color: var(--text-muted);
            color: var(--background);

            &:hover {
              filter: brightness(1.15);
            }
          `
        : css`
            cursor: default;
            border: 1px dashed var(--border);
            color: var(--text-muted);
            opacity: 0.55;
          `}
`;

// Articles title rename input (BlogView TitleEditInput pattern); font matches
// MainTitle so the text doesn't resize when entering edit mode.
const PrimaryVideoInput = styled.input`
  width: 460px;
  max-width: 100%;
  box-sizing: border-box;
  background: var(--input-background);
  ${fieldStateCss}
  border-radius: var(--radius-input);
  color: var(--foreground);
  font-family: var(--font-primary);
  font-size: var(--text-sm);
  font-weight: var(--font-weight-medium);
  padding: 4px 8px;
  outline: none;

  &::selection {
    background: var(--primary-tint);
  }
`;
